import { yupResolver } from '@hookform/resolvers/yup';
import { kebabCase, set } from 'lodash';
import { useCallback, useMemo } from 'react';
import { Resolver, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { InferType } from 'yup';

import { formatTimeToMinutes } from '../../../data/utils/date.utils';
import { FormError } from '../../../data/utils/form.utils';
import { schema } from './schema';

export const makeResolver =
  (
    resolver: Resolver<InferType<typeof schema>>
  ): Resolver<InferType<typeof schema>> =>
  async (values, context, options) => {
    let result = await resolver(values, context, options);

    const startTime = formatTimeToMinutes(values.startTime);
    const endTime = formatTimeToMinutes(values.endTime);

    if (startTime > endTime) {
      result = set(result, 'errors.endTime', {
        message: 'template.entry.error.end-time',
      });
    }

    return result;
  };

export function useTemplateEntryForm(blockSize?: number) {
  const values = useMemo(() => schema.getDefault(), []);

  const resolver = useMemo(() => makeResolver(yupResolver(schema)), []);

  const form = useForm<InferType<typeof schema>>({
    resolver,
    defaultValues: { ...values, minutes: blockSize ?? 15 },
    mode: 'onBlur',
  });

  const intl = useIntl();

  const setErrors = useCallback(
    (errors: FormError[] | null | undefined) => {
      if (errors) {
        errors.forEach(error => {
          const name = error.fieldName as keyof InferType<typeof schema>;

          form.setError(name, {
            message: intl.formatMessage({
              id: `common.errors.${kebabCase(error.message)}`,
              defaultMessage: error.message,
            }),
          });
        });

        return false;
      }

      return true;
    },
    [form, intl]
  );

  return {
    form,
    formIsValid: useMemo(() => form.formState.isValid, [form.formState]),
    setErrors,
  };
}
